
export const PROPOSAL_STATUS_FLOW = [
  "to_do",
  "in_progress",
  "sent",
  "approved_to_be_paid",
  "needs_fix_sales",
  "in_review_to_be_canceled",
  "paid_internal_review",
  "ready_to_book",
  "canceled",
  "booked",
  "awaiting_arrival",
  "in_house",
  "request_feedback",
  "done",
] as const;

export type ProposalStatus = (typeof PROPOSAL_STATUS_FLOW)[number];

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  to_do: "To Do",
  in_progress: "In Progress",
  sent: "Sent",
  approved_to_be_paid: "Approved – To be Paid",
  needs_fix_sales: "Needs Fix – Sales",
  in_review_to_be_canceled: "In Review – To Be Canceled",
  paid_internal_review: "Paid – Internal Review",
  ready_to_book: "Ready to Book",
  canceled: "Canceled",
  booked: "Booked",
  awaiting_arrival: "Awaiting Arrival",
  in_house: "In House",
  request_feedback: "Request Feedback",
  done: "Done",
};

export const PROPOSAL_STATUS_COLORS: Record<ProposalStatus, { bg: string; text: string; cssVar: string }> = {
  to_do: { bg: "217 91% 60%", text: "0 0% 100%", cssVar: "--status-to-do" },
  in_progress: { bg: "217 91% 60%", text: "0 0% 100%", cssVar: "--status-in-progress" },
  sent: { bg: "224 76% 40%", text: "0 0% 100%", cssVar: "--status-sent" },
  approved_to_be_paid: { bg: "45 93% 56%", text: "30 10% 15%", cssVar: "--status-approved-to-be-paid" },
  needs_fix_sales: { bg: "0 84% 60%", text: "0 0% 100%", cssVar: "--status-needs-fix-sales" },
  in_review_to_be_canceled: { bg: "24 95% 53%", text: "0 0% 100%", cssVar: "--status-in-review-to-be-canceled" },
  paid_internal_review: { bg: "38 92% 42%", text: "0 0% 100%", cssVar: "--status-paid-internal-review" },
  ready_to_book: { bg: "160 84% 40%", text: "0 0% 100%", cssVar: "--status-ready-to-book" },
  canceled: { bg: "0 0% 35%", text: "0 0% 100%", cssVar: "--status-canceled" },
  booked: { bg: "160 84% 35%", text: "0 0% 100%", cssVar: "--status-booked" },
  awaiting_arrival: { bg: "262 83% 58%", text: "0 0% 100%", cssVar: "--status-awaiting-arrival" },
  in_house: { bg: "262 83% 48%", text: "0 0% 100%", cssVar: "--status-in-house" },
  request_feedback: { bg: "220 9% 46%", text: "0 0% 100%", cssVar: "--status-request-feedback" },
  done: { bg: "220 9% 64%", text: "220 13% 13%", cssVar: "--status-done" },
};

const LEGACY_STATUS_MAP: Record<string, ProposalStatus> = {
  draft: "in_progress",
  approved: "approved_to_be_paid",
  confirmed: "approved_to_be_paid",
  expired: "done", // Expired proposals are considered done
  estimate_created: "paid_internal_review",
  estimate_manual_required: "approved_to_be_paid",
};

export function mapLegacyStatusToNewStatus(status: string | null | undefined): ProposalStatus {
  if (!status) return "to_do";
  
  const normalizedStatus = status.toLowerCase().replace(/-/g, "_");
  
  if (PROPOSAL_STATUS_FLOW.includes(normalizedStatus as ProposalStatus)) {
    return normalizedStatus as ProposalStatus;
  }
  
  if (LEGACY_STATUS_MAP[normalizedStatus]) {
    return LEGACY_STATUS_MAP[normalizedStatus];
  }
  
  return "to_do";
}

export function getNextStatus(current: ProposalStatus): ProposalStatus | null {
  const index = PROPOSAL_STATUS_FLOW.indexOf(current);
  if (index === -1 || index === PROPOSAL_STATUS_FLOW.length - 1) return null;
  return PROPOSAL_STATUS_FLOW[index + 1];
}

export function getPreviousStatus(current: ProposalStatus): ProposalStatus | null {
  const index = PROPOSAL_STATUS_FLOW.indexOf(current);
  if (index <= 0) return null;
  return PROPOSAL_STATUS_FLOW[index - 1];
}

export function isValidTransition(from: ProposalStatus, to: ProposalStatus): boolean {
  const fromIndex = PROPOSAL_STATUS_FLOW.indexOf(from);
  const toIndex = PROPOSAL_STATUS_FLOW.indexOf(to);
  return fromIndex !== -1 && toIndex !== -1 && toIndex === fromIndex + 1;
}

export function getStatusIndex(status: ProposalStatus): number {
  return PROPOSAL_STATUS_FLOW.indexOf(status);
}

export function isPubliclyAccessible(status: ProposalStatus): boolean {
  const index = getStatusIndex(status);
  const sentIndex = getStatusIndex("sent");
  return index >= sentIndex;
}

export function canShowShareButton(status: string | null | undefined): boolean {
  const normalizedStatus = mapLegacyStatusToNewStatus(status);
  if (normalizedStatus === "in_review_to_be_canceled" || normalizedStatus === "canceled") {
    return false;
  }
  return isPubliclyAccessible(normalizedStatus);
}

export function isFinancialStatus(status: ProposalStatus): boolean {
  return ["approved_to_be_paid", "paid_internal_review"].includes(status);
}

export function isOperationalStatus(status: ProposalStatus): boolean {
  return ["ready_to_book", "booked", "awaiting_arrival", "in_house"].includes(status);
}

export function isCompletedStatus(status: ProposalStatus): boolean {
  return ["request_feedback", "done"].includes(status);
}

export function isPaidOrBeyond(status: string | null | undefined): boolean {
  const normalizedStatus = mapLegacyStatusToNewStatus(status);
  if (normalizedStatus === "in_review_to_be_canceled" || normalizedStatus === "canceled") {
    return true;
  }
  const paidIndex = getStatusIndex("paid_internal_review");
  const currentIndex = getStatusIndex(normalizedStatus);
  return currentIndex >= paidIndex;
}

// Mirrors ProposalService.LOCKED_STATUSES on the backend: the statuses that freeze a proposal
// for editing. The CEO can reopen one of these via the edit_unlocked flag, which deliberately
// leaves the status untouched.
export const EDIT_LOCKED_STATUSES: ProposalStatus[] = [
  "approved_to_be_paid",
  "in_review_to_be_canceled",
  "paid_internal_review",
  "ready_to_book",
  "booked",
  "awaiting_arrival",
  "in_house",
  "request_feedback",
  "done",
  "canceled",
];

export function statusLocksEditing(status: string | null | undefined): boolean {
  return EDIT_LOCKED_STATUSES.includes(mapLegacyStatusToNewStatus(status));
}

export function getStatusLabel(status: string | null | undefined): string {
  const normalizedStatus = mapLegacyStatusToNewStatus(status);
  return PROPOSAL_STATUS_LABELS[normalizedStatus] || status || "Unknown";
}

export function getStatusColor(status: string | null | undefined): { bg: string; text: string; cssVar: string } {
  const normalizedStatus = mapLegacyStatusToNewStatus(status);
  return PROPOSAL_STATUS_COLORS[normalizedStatus] || PROPOSAL_STATUS_COLORS.to_do;
}
